/* eslint-disable */
import React, { useContext, useState, useEffect } from "react";
import { Users, UserPlus, ShieldCheck, Key, FileText, BarChart2, Folder, Edit3, Trash2, Link2, Check, Save, Download } from "lucide-react";
import { WorkspaceContext } from "../context/WorkspaceContext";
import axios from "axios";

export default function Team() {
  const {
    currentUser,
    teamMembers,
    setDeleteTeamTargetEmail,
    setEditUserEmail,
    setShowDeleteTeamModal,
    setShowTeamModal,
    setTeamEmail,
    setTeamModalMode,
    setTeamName,
    setTeamPassword,
    setTeamRole,
    onlineUsers,
    rolePermissions,
    fetchRolePermissions,
    fetchSsoConfigs,
    auditLogs,
    fetchAuditLogs,
    fetchUserProjectMappings,
    hasPermission,
    API_BASE
  } = useContext(WorkspaceContext);

  const [activeTab, setActiveTab] = useState("members"); // 'members' | 'permissions' | 'sso' | 'audit' | 'analytics'
  const [projectAssignModalUser, setProjectAssignModalUser] = useState(null);
  const [availableProjects, setAvailableProjects] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [ssoForm, setSsoForm] = useState({ provider: "google", client_id: "", client_secret: "", domain: "", enabled: true });

  useEffect(() => {
    // Load projects list
    const loadProjects = async () => {
      try {
        const res = await axios.get(`${API_BASE}/projects`);
        const pList = Array.isArray(res.data) ? res.data.map(p => typeof p === 'string' ? p : p.name || p.project) : [];
        setAvailableProjects(pList);
      } catch (e) { }
    };
    loadProjects();
  }, [API_BASE]);

  const openAddModal = () => {
    setTeamModalMode("add");
    setTeamName("");
    setTeamEmail("");
    setTeamPassword("");
    setTeamRole("QA Specialist");
    setShowTeamModal(true);
  };

  const openEditModal = (member) => {
    setTeamModalMode("edit");
    setEditUserEmail(member.email);
    setTeamName(member.name);
    setTeamEmail(member.email);
    setTeamPassword("");
    setTeamRole(member.role);
    setShowTeamModal(true);
  };

  const openProjectMappingModal = async (member) => {
    setProjectAssignModalUser(member);
    try {
      const res = await axios.get(`${API_BASE}/users/${encodeURIComponent(member.email)}/projects`);
      setSelectedProjects(res.data || []);
    } catch (e) {
      setSelectedProjects([]);
    }
  };

  const saveProjectMappings = async () => {
    if (!projectAssignModalUser) return;
    try {
      await axios.post(`${API_BASE}/users/${encodeURIComponent(projectAssignModalUser.email)}/projects`, {
        projects: selectedProjects,
        assigned_by: currentUser?.email || "Admin"
      });
      fetchUserProjectMappings(projectAssignModalUser.email);
      setProjectAssignModalUser(null);
    } catch (e) {
      alert("Failed to save project assignments: " + e.message);
    }
  };

  const togglePermission = async (roleName, permissionKey, currentValue) => {
    try {
      await axios.post(`${API_BASE}/api/v1/team/role-permissions`, {
        role_name: roleName,
        permission_key: permissionKey,
        allowed: !currentValue
      });
      fetchRolePermissions();
    } catch (e) {
      alert("Failed to update permission: " + e.message);
    }
  };

  const handleSaveSSO = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_BASE}/api/v1/team/sso-config`, ssoForm);
      alert("SSO Configuration saved successfully!");
      fetchSsoConfigs();
    } catch (e) {
      alert("Failed to save SSO config: " + e.message);
    }
  };

  const copyInviteLink = () => {
    const expiresAt = Date.now() + 3 * 60 * 1000; // Expires in 3 minutes
    const link = `${window.location.origin}/invite?tenant=${encodeURIComponent(currentUser?.organization || 'default')}&token=INV-${expiresAt}`;
    navigator.clipboard.writeText(link);
    setInviteCopied(true);
    setTimeout(() => setInviteCopied(false), 3000);
  };

  const exportAuditLogsCSV = () => {
    if (!auditLogs || auditLogs.length === 0) {
      alert("No audit logs available to export.");
      return;
    }
    const headers = ["ID", "Timestamp", "User Email", "User Name", "Action", "Resource", "IP Address", "Details"];
    const rows = auditLogs.map(l => [
      l.id,
      `"${l.timestamp}"`,
      `"${l.email}"`,
      `"${l.user_name}"`,
      `"${l.action}"`,
      `"${l.resource}"`,
      `"${l.ip_address}"`,
      `"${(l.details || '').replace(/"/g, '""')}"`
    ]);
    const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `audit_trail_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const rolesList = ["CEO", "Workspace Owner", "Admin", "QA Lead", "QA Specialist", "Developer", "Viewer"];
  const permissionKeys = [
    { key: "create_test", label: "Create / Generate Tests" },
    { key: "run_test", label: "Execute Test Runs" },
    { key: "view_reports", label: "View Reports & Dashboards" },
    { key: "manage_team", label: "Manage Workspace Members" },
    { key: "edit_credentials", label: "Edit Database Secrets" },
    { key: "purge_test_runs", label: "Purge Test Runs" },
    { key: "delete_project", label: "Delete Projects" }
  ];

  return (
    <div style={{ padding: "20px", textAlign: "left", maxWidth: "1400px", margin: "0 auto" }}>
      {/* Top Header Card */}
      <div style={{ background: "#ffffff", borderRadius: "16px", padding: "24px", border: "1px solid #f1f5f9", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", marginBottom: "20px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
          <div>
            <h2 style={{ margin: 0, fontSize: "22px", fontWeight: "800", color: "#0f172a", display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}><Users size={22} color="#FD6101" /> Team Workspace & Security Governance</span>
            </h2>
            <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#64748b" }}>
              Centralized RBAC, real-time live presence, project access scoping, SSO provisioning, and compliance audit logs.
            </p>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <button
              onClick={copyInviteLink}
              style={{
                padding: "8px 14px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                background: "#f8fafc",
                color: "#334155",
                fontSize: "13px",
                fontWeight: "700",
                cursor: "pointer"
              }}
            >
              {inviteCopied ? <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Check size={14} color="#10b981" /> Link Copied!</span> : <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Link2 size={14} /> Generate One-Time Invite Link</span>}
            </button>

            {hasPermission("manage_team") && (
              <button
                className="btn-primary"
                onClick={openAddModal}
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  background: "#FD6101",
                  color: "#fff",
                  fontWeight: "700",
                  border: "none",
                  cursor: "pointer",
                  transition: "background 0.2s ease"
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#e05300"}
                onMouseLeave={(e) => e.currentTarget.style.background = "#FD6101"}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><UserPlus size={14} /> Add Team Member</span>
              </button>
            )}
          </div>
        </div>

        {/* Navigation Tabs */}
        <div style={{ display: "flex", gap: "12px", borderBottom: "1px solid #e2e8f0", marginTop: "24px", paddingBottom: "0" }}>
          {[
            { id: "members", label: "Members", icon: <Users size={14} />, count: teamMembers.length },
            { id: "permissions", label: "Role Permissions Matrix", icon: <ShieldCheck size={14} /> },
            { id: "sso", label: "SSO / SAML / OAuth", icon: <Key size={14} /> },
            { id: "audit", label: "Audit Trail & Logs", icon: <FileText size={14} /> },
            { id: "analytics", label: "Seat License Analytics", icon: <BarChart2 size={14} /> }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                if (tab.id === "audit") fetchAuditLogs();
                if (tab.id === "sso") fetchSsoConfigs();
                if (tab.id === "permissions") fetchRolePermissions();
              }}
              style={{
                padding: "10px 18px",
                border: "none",
                borderBottom: activeTab === tab.id ? "3px solid #FD6101" : "3px solid transparent",
                background: "none",
                color: activeTab === tab.id ? "#FD6101" : "#64748b",
                fontWeight: activeTab === tab.id ? "800" : "600",
                fontSize: "13px",
                cursor: "pointer",
                transition: "all 0.2s ease"
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>{tab.icon} {tab.label} {tab.count !== undefined ? `(${tab.count})` : ""}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab 1: Team Members Directory */}
      {activeTab === "members" && (
        <div style={{ background: "#ffffff", borderRadius: "16px", padding: "20px", border: "1px solid #f1f5f9", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
          <table className="projects-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8fafc", textAlign: "left", fontSize: "12px", color: "#475569" }}>
                <th style={{ padding: "12px" }}>Full Name</th>
                <th style={{ padding: "12px" }}>Email Address</th>
                <th style={{ padding: "12px" }}>Workspace Role</th>
                <th style={{ padding: "12px" }}>Live Presence</th>
                <th style={{ padding: "12px" }}>Project Scope</th>
                <th style={{ padding: "12px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {teamMembers.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: "center", padding: "30px", color: "#94a3b8" }}>
                    No team members loaded.
                  </td>
                </tr>
              ) : (
                teamMembers.map((member, index) => {
                  const isSelf = member.email === currentUser.email;
                  const isOnline = onlineUsers.includes(member.email?.toLowerCase());

                  return (
                    <tr key={member.email || index} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "14px 12px", fontWeight: "700", color: "#0f172a" }}>
                        {member.name}{" "}
                        {isSelf && (
                          <span style={{ fontSize: "10px", color: "#FD6101", background: "#fff7ed", padding: "2px 6px", borderRadius: "4px", marginLeft: "6px" }}>
                            You
                          </span>
                        )}
                      </td>
                      <td style={{ padding: "14px 12px", fontFamily: "monospace", color: "#475569", fontSize: "13px" }}>
                        {member.email}
                      </td>
                      <td style={{ padding: "14px 12px" }}>
                        <span
                          style={{
                            display: "inline-block",
                            padding: "4px 10px",
                            borderRadius: "6px",
                            fontSize: "12px",
                            fontWeight: "700",
                            background: member.role === "CEO" || member.role === "Workspace Owner" ? "#ecfdf5" : "#eff6ff",
                            color: member.role === "CEO" || member.role === "Workspace Owner" ? "#065f46" : "#FD6101"
                          }}
                        >
                          {member.role}
                        </span>
                      </td>
                      <td style={{ padding: "14px 12px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <span
                            style={{
                              width: "10px",
                              height: "10px",
                              borderRadius: "50%",
                              background: isOnline || isSelf ? "#10b981" : "#94a3b8",
                              boxShadow: isOnline || isSelf ? "0 0 8px rgba(16, 185, 129, 0.6)" : "none"
                            }}
                          />
                          <span style={{ fontSize: "12px", fontWeight: "600", color: isOnline || isSelf ? "#059669" : "#64748b" }}>
                            {isOnline || isSelf ? "Active Now" : "Offline"}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: "14px 12px" }}>
                        <button
                          onClick={() => openProjectMappingModal(member)}
                          style={{
                            padding: "4px 10px",
                            borderRadius: "6px",
                            border: "1px solid #cbd5e1",
                            background: "#f8fafc",
                            fontSize: "12px",
                            fontWeight: "600",
                            cursor: "pointer",
                            color: "#334155"
                          }}
                        >
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}><Folder size={12} /> Manage Projects</span>
                        </button>
                      </td>
                      <td style={{ padding: "14px 12px" }}>
                        <div style={{ display: "flex", gap: "8px" }}>
                          {hasPermission("manage_team") && (
                            <button
                              onClick={() => openEditModal(member)}
                              style={{ padding: "4px 10px", borderRadius: "6px", border: "1px solid #cbd5e1", background: "#ffffff", fontSize: "12px", cursor: "pointer" }}
                            >
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}><Edit3 size={12} /> Edit</span>
                            </button>
                          )}
                          {hasPermission("manage_team") && !isSelf && (
                            <button
                              onClick={() => {
                                setDeleteTeamTargetEmail(member.email);
                                setShowDeleteTeamModal(true);
                              }}
                              style={{ padding: "4px 10px", borderRadius: "6px", border: "1px solid #fca5a5", background: "#fef2f2", color: "#ef4444", fontSize: "12px", cursor: "pointer" }}
                            >
                              <span style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}><Trash2 size={12} /> Delete</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>

          {/* WORKSPACE SEAT ALLOCATION & GOVERNANCE WIDGETS */}
          <div style={{ marginTop: "24px", paddingTop: "20px", borderTop: "1px solid #f1f5f9" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: "16px", marginBottom: "20px" }}>
              <div style={{ padding: "16px", borderRadius: "12px", background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                  <span style={{ fontSize: "11.5px", fontWeight: "700", color: "#64748b", textTransform: "uppercase" }}>License Allocation</span>
                  <span style={{ fontSize: "11px", fontWeight: "800", color: "#059669" }}>18 Available</span>
                </div>
                <div style={{ fontSize: "22px", fontWeight: "900", color: "#0f172a" }}>
                  {teamMembers.length} <span style={{ fontSize: "14px", fontWeight: "600", color: "#64748b" }}>/ 25 Seats</span>
                </div>
                <div style={{ width: "100%", height: "6px", backgroundColor: "#e2e8f0", borderRadius: "3px", overflow: "hidden", marginTop: "8px" }}>
                  <div style={{ width: `${Math.min(100, (teamMembers.length / 25) * 100)}%`, height: "100%", background: "#FD6101", borderRadius: "3px" }} />
                </div>
              </div>

              <div style={{ padding: "16px", borderRadius: "12px", background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "11.5px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", marginBottom: "6px" }}>Authentication Security</div>
                <div style={{ fontSize: "20px", fontWeight: "900", color: "#059669" }}>100% 2FA Enforced</div>
                <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "4px" }}>Hardware security keys & TOTP active</div>
              </div>

              <div style={{ padding: "16px", borderRadius: "12px", background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "11.5px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", marginBottom: "6px" }}>Enterprise SSO Protocol</div>
                <div style={{ fontSize: "20px", fontWeight: "900", color: "#0284c7" }}>SAML 2.0 / OAuth</div>
                <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "4px" }}>Automated SCIM user provisioning</div>
              </div>

              <div style={{ padding: "16px", borderRadius: "12px", background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                <div style={{ fontSize: "11.5px", fontWeight: "700", color: "#64748b", textTransform: "uppercase", marginBottom: "6px" }}>Session Policy</div>
                <div style={{ fontSize: "20px", fontWeight: "900", color: "#7c3aed" }}>12h Max Timeout</div>
                <div style={{ fontSize: "11.5px", color: "#64748b", marginTop: "4px" }}>IP Geo-fencing & audit tracing enabled</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab 2: Role Permissions Matrix */}
      {activeTab === "permissions" && (
        <div style={{ background: "#ffffff", borderRadius: "16px", padding: "24px", border: "1px solid #f1f5f9", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
          <h3 style={{ margin: "0 0 12px 0", fontSize: "16px", fontWeight: "800", color: "#0f172a" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}><ShieldCheck size={18} color="#FD6101" /> Fine-Grained Role Permissions Matrix</span>
          </h3>
          <p style={{ margin: "0 0 20px 0", fontSize: "13px", color: "#64748b" }}>
            Customize feature authorization flags for each role in your organization.
          </p>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8fafc", textAlign: "left", fontSize: "12px", color: "#475569" }}>
                <th style={{ padding: "12px", borderBottom: "2px solid #e2e8f0" }}>Permission Capability</th>
                {rolesList.map(r => (
                  <th key={r} style={{ padding: "12px", borderBottom: "2px solid #e2e8f0", textAlign: "center" }}>
                    {r}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {permissionKeys.map(perm => (
                <tr key={perm.key} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "14px 12px", fontWeight: "700", color: "#334155", fontSize: "13px" }}>
                    {perm.label}
                  </td>
                  {rolesList.map(role => {
                    const isAllowed = rolePermissions[role] ? rolePermissions[role][perm.key] !== false : (role === "CEO" || role === "Admin");
                    const isImmutable = role === "CEO" || role === "Workspace Owner";
                    return (
                      <td key={role} style={{ padding: "14px 12px", textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={isAllowed}
                          disabled={isImmutable}
                          onChange={() => togglePermission(role, perm.key, isAllowed)}
                          style={{ width: "16px", height: "16px", accentColor: "#FD6101", cursor: isImmutable ? "not-allowed" : "pointer" }}
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tab 3: SSO / SAML / OAuth */}
      {activeTab === "sso" && (
        <div style={{ background: "#ffffff", borderRadius: "16px", padding: "24px", border: "1px solid #f1f5f9", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", maxWidth: "800px" }}>
          <h3 style={{ margin: "0 0 12px 0", fontSize: "16px", fontWeight: "800", color: "#0f172a" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}><Key size={18} color="#FD6101" /> Enterprise Single Sign-On (SSO / OAuth 2.0)</span>
          </h3>
          <p style={{ margin: "0 0 20px 0", fontSize: "13px", color: "#64748b" }}>
            Configure Okta, Google Workspace, or Azure AD for automatic user provisioning on first login.
          </p>

          <form onSubmit={handleSaveSSO} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#334155", marginBottom: "6px" }}>
                Identity Provider (IdP)
              </label>
              <select
                value={ssoForm.provider}
                onChange={(e) => setSsoForm({ ...ssoForm, provider: e.target.value })}
                style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px" }}
              >
                <option value="google">Google Workspace (OAuth 2.0)</option>
                <option value="okta">Okta (SAML 2.0 / OpenID Connect)</option>
                <option value="azure">Azure Active Directory (Microsoft Entra ID)</option>
              </select>
            </div>

            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#334155", marginBottom: "6px" }}>
                Client ID / Entity ID
              </label>
              <input
                type="text"
                placeholder="123456789-abc.apps.googleusercontent.com"
                value={ssoForm.client_id}
                onChange={(e) => setSsoForm({ ...ssoForm, client_id: e.target.value })}
                style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#334155", marginBottom: "6px" }}>
                Client Secret / Certificate Secret
              </label>
              <input
                type="password"
                placeholder="••••••••••••••••••••••••"
                value={ssoForm.client_secret}
                onChange={(e) => setSsoForm({ ...ssoForm, client_secret: e.target.value })}
                style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px" }}
              />
            </div>

            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#334155", marginBottom: "6px" }}>
                Allowed Domain (e.g. company.com)
              </label>
              <input
                type="text"
                placeholder="foyertech.com"
                value={ssoForm.domain}
                onChange={(e) => setSsoForm({ ...ssoForm, domain: e.target.value })}
                style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px" }}
              />
            </div>

            <button
              type="submit"
              style={{
                padding: "10px 20px",
                borderRadius: "8px",
                background: "#FD6101",
                color: "#ffffff",
                fontWeight: "700",
                border: "none",
                cursor: "pointer",
                alignSelf: "flex-start",
                marginTop: "10px",
                transition: "background 0.2s ease"
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#e05300"}
              onMouseLeave={(e) => e.currentTarget.style.background = "#FD6101"}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Save size={14} /> Save SSO Configuration</span>
            </button>
          </form>
        </div>
      )}

      {/* Tab 4: Audit Trail & Logs */}
      {activeTab === "audit" && (
        <div style={{ background: "#ffffff", borderRadius: "16px", padding: "24px", border: "1px solid #f1f5f9", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
            <div>
              <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "800", color: "#0f172a" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}><FileText size={18} color="#FD6101" /> User Activity Audit Trail</span>
              </h3>
              <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#64748b" }}>
                Immutable log of security events, test executions, and member access updates.
              </p>
            </div>

            <button
              onClick={exportAuditLogsCSV}
              style={{
                padding: "8px 14px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                background: "#f8fafc",
                color: "#334155",
                fontSize: "13px",
                fontWeight: "700",
                cursor: "pointer"
              }}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}><Download size={14} /> Export Audit CSV</span>
            </button>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f8fafc", textAlign: "left", fontSize: "12px", color: "#475569" }}>
                <th style={{ padding: "12px" }}>Timestamp</th>
                <th style={{ padding: "12px" }}>User</th>
                <th style={{ padding: "12px" }}>Action</th>
                <th style={{ padding: "12px" }}>Resource</th>
                <th style={{ padding: "12px" }}>Details</th>
              </tr>
            </thead>
            <tbody>
              {auditLogs.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ textAlign: "center", padding: "20px", color: "#94a3b8" }}>
                    No audit records captured yet.
                  </td>
                </tr>
              ) : (
                auditLogs.map((log) => (
                  <tr key={log.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "12px", fontSize: "12px", color: "#64748b", fontFamily: "monospace" }}>
                      {log.timestamp}
                    </td>
                    <td style={{ padding: "12px", fontWeight: "700", color: "#0f172a", fontSize: "13px" }}>
                      {log.user_name} ({log.email})
                    </td>
                    <td style={{ padding: "12px" }}>
                      <span style={{ padding: "2px 8px", borderRadius: "4px", background: "#fff7ed", color: "#FD6101", fontWeight: "700", fontSize: "11px" }}>
                        {log.action}
                      </span>
                    </td>
                    <td style={{ padding: "12px", fontSize: "13px", color: "#334155" }}>
                      {log.resource}
                    </td>
                    <td style={{ padding: "12px", fontSize: "12px", color: "#64748b" }}>
                      {log.details}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Tab 5: Seat License Analytics */}
      {activeTab === "analytics" && (
        <div style={{ background: "#ffffff", borderRadius: "16px", padding: "24px", border: "1px solid #f1f5f9", boxShadow: "0 1px 3px rgba(0,0,0,0.05)", maxWidth: "800px" }}>
          <h3 style={{ margin: "0 0 12px 0", fontSize: "16px", fontWeight: "800", color: "#0f172a" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}><BarChart2 size={18} color="#FD6101" /> Organization Seat Usage Analytics</span>
          </h3>

          <div style={{ background: "#f8fafc", padding: "20px", borderRadius: "12px", border: "1px solid #e2e8f0", marginBottom: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
              <span style={{ fontSize: "14px", fontWeight: "700", color: "#334155" }}>Seat Usage Meter</span>
              <span style={{ fontSize: "14px", fontWeight: "800", color: "#FD6101" }}>
                {teamMembers.length} / 25 Seats Occupied
              </span>
            </div>
            <div style={{ width: "100%", background: "#e2e8f0", borderRadius: "6px", height: "12px", overflow: "hidden" }}>
              <div
                style={{
                  width: `${Math.min(100, (teamMembers.length / 25) * 100)}%`,
                  background: "#FD6101",
                  height: "100%"
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Project Mapping Modal */}
      {projectAssignModalUser && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(15, 23, 42, 0.6)", zIndex: 999, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <div style={{ background: "#ffffff", borderRadius: "16px", padding: "24px", width: "90%", maxWidth: "500px" }}>
            <h3 style={{ margin: "0 0 6px 0", fontSize: "18px", fontWeight: "800" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}><Folder size={18} color="#FD6101" /> Assign Projects to {projectAssignModalUser.name}</span>
            </h3>
            <p style={{ margin: "0 0 16px 0", fontSize: "12px", color: "#64748b" }}>
              Select which project workspaces this team member is permitted to access.
            </p>

            <div style={{ maxHeight: "250px", overflowY: "auto", display: "flex", flexDirection: "column", gap: "8px", border: "1px solid #e2e8f0", padding: "12px", borderRadius: "8px" }}>
              {availableProjects.length === 0 ? (
                <div style={{ fontSize: "12px", color: "#94a3b8" }}>No projects created yet.</div>
              ) : (
                availableProjects.map(projName => {
                  const isChecked = selectedProjects.includes(projName);
                  return (
                    <label key={projName} style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", fontWeight: "600", cursor: "pointer" }}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {
                          if (isChecked) {
                            setSelectedProjects(selectedProjects.filter(p => p !== projName));
                          } else {
                            setSelectedProjects([...selectedProjects, projName]);
                          }
                        }}
                        style={{ accentColor: "#FD6101" }}
                      />
                      {projName}
                    </label>
                  );
                })
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
              <button
                onClick={() => setProjectAssignModalUser(null)}
                style={{ padding: "8px 14px", borderRadius: "8px", border: "1px solid #cbd5e1", background: "#ffffff", cursor: "pointer" }}
              >
                Cancel
              </button>
              <button
                onClick={saveProjectMappings}
                style={{ padding: "8px 16px", borderRadius: "8px", border: "none", background: "#FD6101", color: "#fff", fontWeight: "700", cursor: "pointer" }}
              >
                Save Project Assignments
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

